import { useCallback, useEffect, useState, useRef } from 'react';
import { DateTime } from 'luxon';
import { debounce } from 'lodash';
import { OrderInputRental } from '../../../../clients/quipli-service/order/order-calculation';
import { CustomOrderPriceRequest } from '../../../../clients/quipli-service/pricing/pricing';
import { toast } from '../../../../util/toasts';
import { Location } from '../../../../clients/quipli-service/location/location';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import usePrevious from '../../../../hooks/use-previous';
import { ProductItemDataReturn } from './use-product-item-data';
import { UseProductItemAvailabilityReturn } from './use-product-item-availability';
import { useRentalPriceCalculator } from './use-rental-price-calculator';

export interface UseProductItemDatesProps {
  productId: number;
  id?: number;
  startDate?: DateTime;
  endDate?: DateTime;
  isNewProduct: boolean;
  quantity: number;
  customRentalRates: CustomOrderPriceRequest[];
  orderId: number;
  taxCodeId?: string;
  location: Location;
  isTaxInclusive: boolean;
  isOrderCycleBilled: boolean;
  cycleBillingTerms?: { cycleLength: any };
  updateInputItem: ProductItemDataReturn['updateInputItem'];
  setEditsValid: (valid: boolean) => void;
  checkAvailableQuantity: UseProductItemAvailabilityReturn['checkAvailableQuantity'];
}

export interface ProductItemDatesReturn {
  startDateInput: DateTime;
  endDateInput: DateTime | undefined;
  isEndDateInputValid: boolean;
  updateStartDate: (date: DateTime) => void;
  updateEndDate: (date: DateTime | undefined) => void;
}

export const useProductItemDates = ({
  productId,
  startDate,
  endDate,
  id,
  isNewProduct,
  quantity,
  customRentalRates,
  orderId,
  taxCodeId,
  location,
  isTaxInclusive,
  isOrderCycleBilled,
  cycleBillingTerms,
  updateInputItem,
  setEditsValid,
  checkAvailableQuantity,
}: UseProductItemDatesProps) => {
  const { calculateRentalPrice } = useRentalPriceCalculator({
    location,
    isTaxInclusive,
    isOrderCycleBilled,
    cycleBillingTerms,
  });

  // Local date state for immediate UI updates
  const [startDateInput, setStartDateInput] = useState<DateTime>(startDate || DateTime.now());
  const [endDateInput, setEndDateInput] = useState<DateTime | undefined>(endDate);
  const [isEndDateInputValid, setIsEndDateInputValid] = useState<boolean>(true);

  const prevStart = usePrevious(startDateInput);
  const prevEnd = usePrevious(endDateInput);

  // Use ref to get the latest customRentalRates value (this one needs it due to complex dependency chain)
  const customRentalRatesRef = useRef(customRentalRates);
  customRentalRatesRef.current = customRentalRates;

  // Sync local state with props
  useEffect(() => {
    if (startDate && (!startDateInput || +startDateInput !== +startDate)) {
      setStartDateInput(startDate);
    }
  }, [startDate]);

  useEffect(() => {
    if (endDate && endDateInput && +endDateInput !== +endDate) {
      setEndDateInput(endDate);
    }
  }, [endDate]);

  // Validate edits
  useEffect(() => {
    setEditsValid(isEndDateInputValid);
  }, [isEndDateInputValid]);

  // Get the new rental price when dates are changed
  const updateRentalPriceDates = useCallback(
    debounce(async (dates: { start: DateTime; end?: DateTime }, type: 'start' | 'end') => {
      if (!productId && !id) {
        return;
      }

      let payload: Partial<OrderInputRental> = {};

      if (!isNewProduct) {
        // Only update price if dates actually changed from their previous values
        // Avoid price updates on initial render when prevStart/prevEnd are undefined
        const startChanged = prevStart && !prevStart.equals(dates.start);
        const endChanged =
          (prevEnd !== undefined || dates.end !== undefined) &&
          (prevEnd === undefined || dates.end === undefined || !prevEnd.equals(dates.end));
        const hasValidPreviousDates = !!prevStart || !!prevEnd;

        if (hasValidPreviousDates && (startChanged || endChanged)) {
          const priceResult = await calculateRentalPrice({
            productId,
            orderProductId: id,
            type: OrderType.Rental,
            start: dates.start,
            end: dates.end,
            orderId,
            taxCodeId,
            customOrderRentalRates: customRentalRatesRef.current.filter((r) => {
              return !!(r as any).originalPrice?.id || !!r.action;
            }),
          });

          if (priceResult.error) {
            return; // Error already toasted by calculator
          }

          payload = {
            ...payload,
            start: dates.start,
            customPrice: priceResult.customPrice,
          };
        } else {
          // Dates didn't change, just update dates without touching price
          payload = {
            ...payload,
            start: dates.start,
          };
        }
      }

      payload = {
        ...payload,
        end: type === 'start' ? prevEnd : dates.end,
      };

      // Check availability based on new dates and quantity
      checkAvailableQuantity(quantity, { start: payload.start!, end: payload.end });

      updateInputItem(payload);
    }, 200),
    [
      updateInputItem,
      quantity,
      isNewProduct,
      prevStart,
      prevEnd,
      productId,
      id,
      isOrderCycleBilled,
      cycleBillingTerms,
      calculateRentalPrice,
      checkAvailableQuantity,
    ],
  );

  const changeStart = (date: DateTime) => {
    updateRentalPriceDates(
      {
        start: date,
        end: endDateInput,
      },
      'start',
    );
  };

  const changeEnd = (date: DateTime | undefined) => {
    updateRentalPriceDates(
      {
        start: startDateInput,
        end: date,
      },
      'end',
    );
  };

  const handleStartDateChange = useCallback(
    (date: DateTime) => {
      setStartDateInput(date);
      if (endDateInput && date && date > endDateInput) {
        setIsEndDateInputValid(false);
        toast('End date cannot be before start date, please adjust.', 'warning');
      } else {
        setIsEndDateInputValid(true);
        changeStart(date);
      }
    },
    [endDateInput, changeStart],
  );

  const handleEndDateChange = useCallback(
    (date: DateTime | undefined) => {
      setEndDateInput(date);
      if (startDateInput && date && date < startDateInput) {
        setIsEndDateInputValid(false);
        toast('End date cannot be before start date, please adjust.', 'warning');
      } else {
        setIsEndDateInputValid(true);
        changeEnd(date);
      }
    },
    [startDateInput, changeEnd],
  );

  return {
    startDateInput,
    endDateInput,
    isEndDateInputValid,
    updateStartDate: handleStartDateChange,
    updateEndDate: handleEndDateChange,
  };
};
